/// <reference types="vite-plugin-svgr/client" />
import * as Toast from '@radix-ui/react-toast';
import type { ToastProps } from '@radix-ui/react-toast';
import React, { useState } from 'react';
import { keyframes, styled } from 'styled-components';
import CloseIcon from '@resources/icons/CloseIcon.svg?react';
import Styles from '@themes/Styles';

const slideInAnimation = keyframes`
    from {
      transform: translateX(calc(100% + 25px));
    }
    to {
      transform: translateX(0);
    }
`;

const hideAnimation = keyframes`
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
`;

const StyledRoot = styled(Toast.Root)`
  background-color: ${({ theme }) => theme.backgroundPrimary};
  border-radius: ${Styles.borderRadiusSharp};
  box-shadow: ${({ theme }) => theme.boxShadow} ${({ theme }) => theme.boxShadowColor};
  padding: 8px 16px;

  display: flex;
  flex-direction: column;
  align-items: stretch;

  &[data-state='open'] {
    animation: ${slideInAnimation} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state='closed'] {
    animation: ${hideAnimation} 150ms ease-in;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const Body = styled.div`
  display: block;
  margin-bottom: 8px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const StyledAction = styled(Toast.Action)`
  background: ${Styles.buttonFillBlue};
  color: white;
  border: 0;
  border-radius: ${Styles.borderRadiusSharp};
  padding: 6px;
  cursor: pointer;
`;

const StyledClose = styled(Toast.Close)`
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
`;

const StyledCloseIcon = styled(CloseIcon)`
  fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledTitle = styled(Toast.Title)`
  flex: 1;
  align-self: center;
  color: ${({ theme }) => theme.textColorPrimary};
  font-weight: bold;
`;

const StyledDescription = styled(Toast.Description)`
  flex: 1;
  color: ${({ theme }) => theme.textColorPrimary};
  font-size: 12px;
`;

type LifeAtToastV2Props = {
  /** Props for the toast root component */
  root?: Omit<ToastProps, 'open' | 'onOpenChange'>;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Props for the action button */
  action?: {
    content: React.ReactNode;
    onClick?: () => void;
    altText: string;
  };
};

export default function LifeAtToastV2(props: LifeAtToastV2Props) {
  const [open, setOpen] = useState(true);
  const handleOpenChange = (value: boolean) => {
    setOpen(value);
  };

  return (
    <StyledRoot {...props.root} open={open} onOpenChange={handleOpenChange}>
      <Header>
        <StyledClose>
          <StyledCloseIcon />
        </StyledClose>
      </Header>
      <Body>
        {props.title && <StyledTitle>{props.title}</StyledTitle>}
        {props.description && <StyledDescription>{props.description}</StyledDescription>}
      </Body>
      <Footer>
        {props.action && (
          <StyledAction altText={props.action.altText} onClick={props.action?.onClick}>
            {props.action.content}
          </StyledAction>
        )}
      </Footer>
    </StyledRoot>
  );
}
